import { useEffect, RefObject, useRef, useCallback } from 'react';
import { logInfo, LogCategory } from '@/utils/logger';

/**
 * Hook to automatically scroll to the bottom of a chat interface
 * when new agent responses arrive, using event-based triggers to avoid
 * scrolling during infinite scroll operations
 * 
 * @param chatContainerRef Reference to the chat container element
 * @param bottomRef Reference to an element at the bottom of the chat
 * @param messageCount Current number of messages
 * @param isLoading Whether a message is being loaded/generated
 * @param isLoadingOlder Whether older messages are being loaded (should not auto-scroll)
 * @returns Object with captureScrollPosition function for infinite scroll
 */
export const useChatScroll = (
  chatContainerRef: RefObject<HTMLDivElement>,
  bottomRef: RefObject<HTMLDivElement>,
  messageCount: number,
  isLoading: boolean,
  isLoadingOlder: boolean = false
) => {
  const lastLoggedCount = useRef(0);
  const isFirstLoad = useRef(true);
  const wasNearBottom = useRef(true);
  
  // ✅ SCROLL POSITION PRESERVATION: Handle infinite scroll position restoration
  const scrollPositionRef = useRef<{
    scrollTop: number;
    scrollHeight: number;
    shouldRestore: boolean;
  }>({ scrollTop: 0, scrollHeight: 0, shouldRestore: false });
  
  // ✅ RADIX UI COMPATIBLE: Proper scroll function for ScrollArea viewport
  const scrollToBottom = useCallback((instant = false, shouldLog = false) => {
    const container = chatContainerRef.current;
    if (!container) return;
    
    // Find the Radix ScrollArea viewport element
    const scrollArea = container.closest('[data-radix-scroll-area-root]');
    const viewport = scrollArea?.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement;
    
    if (viewport) {
      // Use Radix UI ScrollArea viewport scrolling
      const scrollToOptions: ScrollToOptions = {
        top: viewport.scrollHeight,
        behavior: instant ? 'auto' : 'smooth'
      };
      
      viewport.scrollTo(scrollToOptions);
      
      if (shouldLog) {
        logInfo('Scrolled to bottom of chat (Radix ScrollArea)', { 
          category: LogCategory.UI,
          data: {
            scrollHeight: viewport.scrollHeight,
            behavior: instant ? 'instant' : 'smooth'
          }
        });
      }
    } else if (bottomRef.current) {
      // Fallback to scrollIntoView for non-ScrollArea elements
      bottomRef.current.scrollIntoView({ 
        behavior: instant ? 'auto' : 'smooth', 
        block: 'end'
      });
      
      if (shouldLog) {
        logInfo('Scrolled to bottom of chat (fallback)', { category: LogCategory.UI });
      }
    }
  }, [chatContainerRef, bottomRef]);

  // Get scroll elements once for performance
  const getScrollElements = useCallback(() => {
    const container = chatContainerRef.current;
    if (!container) return null;
    
    const scrollArea = container.closest('[data-radix-scroll-area-root]');
    const viewport = scrollArea?.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement;
    const scrollableElement = viewport || container.parentElement || container;
    
    return { viewport, scrollableElement };
  }, [chatContainerRef]);
  
  // ✅ INFINITE SCROLL POSITION RESTORATION: Handle older message loading (UNCHANGED)
  useEffect(() => {
    const shouldLog = messageCount !== lastLoggedCount.current && messageCount > 0;
    
    // 🔧 SCROLL POSITION RESTORATION: Handle infinite scroll first
    if (isLoadingOlder && scrollPositionRef.current.shouldRestore) {
      const container = chatContainerRef.current;
      if (container) {
        const scrollArea = container.closest('[data-radix-scroll-area-root]');
        const viewport = scrollArea?.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement;
        
        if (viewport) {
          const { scrollTop: oldScrollTop, scrollHeight: oldScrollHeight } = scrollPositionRef.current;
          
          // Wait for DOM to update, then restore position
          setTimeout(() => {
            const newScrollHeight = viewport.scrollHeight;
            const heightDifference = newScrollHeight - oldScrollHeight;
            const newScrollTop = oldScrollTop + heightDifference;
            
            viewport.scrollTop = newScrollTop;
            scrollPositionRef.current.shouldRestore = false;
            
            // Update wasNearBottom based on restored position
            const { scrollHeight, clientHeight } = viewport;
            wasNearBottom.current = newScrollTop + clientHeight >= scrollHeight - 100;
          }, 0);
        }
      }
      
      if (shouldLog) {
        lastLoggedCount.current = messageCount;
      }
      return;
    }
    
    // Update logged count for other cases
    if (shouldLog) {
      lastLoggedCount.current = messageCount;
    }
  }, [messageCount, isLoadingOlder]);
  
  // 🚀 EVENT-BASED AUTO-SCROLL: Listen for new agent message events
  useEffect(() => {
    const handleNewAgentMessage = (event: CustomEvent) => {
      // Skip if loading older messages (infinite scroll)
      if (isLoadingOlder) return;
      
      // Only scroll if user is near bottom
      if (wasNearBottom.current) {
        setTimeout(() => {
          scrollToBottom(false, true); // Log important new message scrolls
        }, 150); // Delay to ensure DOM is fully updated
        
        logInfo('Auto-scroll triggered by new agent message', {
          category: LogCategory.UI,
          data: {
            messageId: event.detail.messageId,
            assistantName: event.detail.assistantName,
            timestamp: event.detail.timestamp
          }
        });
      }
    };

    // Listen for new agent message events following established pattern
    window.addEventListener('new-agent-message', handleNewAgentMessage as EventListener);
    
    return () => {
      window.removeEventListener('new-agent-message', handleNewAgentMessage as EventListener);
    };
  }, [isLoadingOlder, scrollToBottom]);
  
  // 🚀 TYPING INDICATOR AUTO-SCROLL: Independent trigger for typing indicator
  useEffect(() => {
    // Only scroll for typing indicator if user is near bottom and not loading older messages
    if (isLoading && wasNearBottom.current && !isLoadingOlder) {
      const timeoutId = setTimeout(() => {
        scrollToBottom(false, false); // Don't log typing indicator scrolls
      }, 50);
      
      return () => clearTimeout(timeoutId);
    }
  }, [isLoading, isLoadingOlder, scrollToBottom]);
  
  // Skip first load scroll - let the component handle initial positioning
  useEffect(() => {
    if (isFirstLoad.current && messageCount > 0) {
      isFirstLoad.current = false;
    }
  }, [messageCount]);
  
  // Add a scroll listener to enable "snap to bottom" behavior
  // when user is already near the bottom (UNCHANGED)
  useEffect(() => {
    const container = chatContainerRef.current;
    if (!container) return;
    
    const handleScroll = () => {
      if (!container) return;
      
      // 🔧 FIX: Use the correct Radix ScrollArea viewport element
      const scrollArea = container.closest('[data-radix-scroll-area-root]');
      const viewport = scrollArea?.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement;
      
      if (!viewport) {
        // Fallback to parent element if not using ScrollArea
        const fallbackElement = container.parentElement || container;
        const { scrollTop, scrollHeight, clientHeight } = fallbackElement;
        const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100;
        
        if (isNearBottom && bottomRef.current) {
          scrollToBottom(false, false); // No logging for auto-scroll behavior
        }
        return;
      }
      
      const { scrollTop, scrollHeight, clientHeight } = viewport;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100;
      
      if (isNearBottom && bottomRef.current) {
        scrollToBottom(false, false); // No logging for auto-scroll behavior
      }
    };
    
    // 🔧 FIX: Listen to the correct scrollable element
    const scrollArea = container.closest('[data-radix-scroll-area-root]');
    const viewport = scrollArea?.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement;
    const scrollableElement = viewport || container.parentElement || container;
    
    scrollableElement.addEventListener('scroll', handleScroll);
    
    return () => {
      scrollableElement.removeEventListener('scroll', handleScroll);
    };
  }, [chatContainerRef, bottomRef, scrollToBottom]);

  // ✅ CAPTURE FUNCTION: For components to trigger scroll position capture (UNCHANGED)
  const captureScrollPosition = useCallback(() => {
    const container = chatContainerRef.current;
    if (!container) return;
    
    const scrollArea = container.closest('[data-radix-scroll-area-root]');
    const viewport = scrollArea?.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement;
    
    if (viewport) {
      scrollPositionRef.current = {
        scrollTop: viewport.scrollTop,
        scrollHeight: viewport.scrollHeight,
        shouldRestore: true
      };
    }
  }, [chatContainerRef]);

  return { captureScrollPosition };
};
